export const apps = [
  {
    "name": "Medical Abbreviations: Quick",
    "tagline": "Look it up. Make it yours.",
    "description": "A focused medical abbreviation reference for iPhone and iPad. Search by abbreviation or meaning, browse the alphabetical index, read definitions in a dedicated view, and return to recent lookups. Build My Library with saved entries, folders, subfolders, and your own custom abbreviations. An adaptive iPad layout lets you browse and read side by side.\n\nKeep saved entries, custom terms, and folders in sync through iCloud using the same Apple Account. Lookup and local library features remain available without iCloud. No advertising or tracking.\n\nMedical abbreviations can have several meanings. Confirm the intended meaning in context. This is an informational reference, not a tool for diagnosis or treatment. Do not enter patient-identifying information.",
    "features": [
      "Search by abbreviation or meaning",
      "Dedicated reader, alphabetical index, and recent lookups",
      "My Library with folders and subfolders",
      "Custom abbreviations and descriptions",
      "Library synchronization with iCloud",
      "Adaptive iPad reading layout and undo removal"
    ],
    "version": "2.0",
    "releaseDate": "2026-09-18",
    "slug": "medical-abbreviations",
    "category": "professional",
    "status": "updated",
    "badge": "Recently updated",
    "storeUrl": "https://apps.apple.com/us/app/medical-abbreviations-quick/id375613508",
    "compatibility": "iPhone · iPad",
    "pricing": {
      "en": "$0.99 US · Paid download",
      "es": "$0.99 en EE. UU. · Descarga de pago"
    },
    "taglineEs": "Consúltalo. Hazlo tuyo.",
    "cardLine": "Look up abbreviations, create custom terms, and organize your library with folders and iCloud.",
    "cardLineEs": "Consulta abreviaturas, crea términos propios y organiza tu biblioteca con carpetas e iCloud.",
    "icon": "/images/app-updates-20261016/medical-abbreviations/icon.jpg",
    "screenshots": [
      "/images/app-updates-20261016/medical-abbreviations/01.webp",
      "/images/app-updates-20261016/medical-abbreviations/02.webp",
      "/images/app-updates-20261016/medical-abbreviations/03.webp",
      "/images/app-updates-20261016/medical-abbreviations/04.webp",
      "/images/app-updates-20261016/medical-abbreviations/05.webp"
    ],
    "whatsNew": "Version 2.0: a redesigned reference with a dedicated reader, recent lookups, custom terms, folders and subfolders, iCloud library sync, and an adaptive iPad layout.\n\nReleased 2026-09-18."
  },
  {
    "name": "ICD 10 On the Go 2027",
    "tagline": "Offline reference. A workspace for your coding day.",
    "description": "Search ICD-10-CM by code, diagnosis, abbreviation, or everyday terminology. Refine results with contextual laterality filters, browse the official hierarchy, review coding instructions, and choose available seventh-character extensions.\n\nThe April 2026 reference edition is free, with up to 10 favorites and folders. An active subscription unlocks FY2027, unlimited favorites, personal note editing, iCloud sync, and an ad-free workspace. Eligible previous paid-app owners retain unlimited favorites and no ads; FY2027, note editing, and sync require a subscription. Saved favorites and readable notes remain available after a subscription expires.\n\nFY2027 applies to October 1, 2026–September 30, 2027; the April 2026 edition applies to April 1–September 30, 2026. Choose the edition appropriate to the encounter or discharge date. Reference content supports professional judgment; it does not provide medical advice or guarantee reimbursement.",
    "features": [
      "Free offline April 2026 reference; FY2027 with subscription",
      "Smart search with contextual laterality filters",
      "Official instructions and seventh-character selection",
      "Favorites and folders; unlimited favorites with subscription",
      "Personal note editing and iCloud sync with subscription"
    ],
    "version": "5.0.1",
    "releaseDate": "2026-09-24",
    "slug": "icd10-on-the-go-2026",
    "category": "professional",
    "status": "updated",
    "badge": "Flagship",
    "storeUrl": "https://apps.apple.com/us/app/icd-10-on-the-go-2027/id377273816",
    "compatibility": "iPhone · iPad",
    "pricing": {
      "en": "April 2026 free · FY2027 with subscription",
      "es": "Abril 2026 gratis · FY2027 con suscripción"
    },
    "taglineEs": "Referencia sin conexión para tu jornada de codificación.",
    "cardLine": "Offline code lookup with laterality filters, seventh-character guidance, and a subscription workspace for FY2027.",
    "cardLineEs": "Búsqueda sin conexión con filtros de lateralidad, séptimo carácter y acceso a FY2027 mediante suscripción.",
    "icon": "/images/app-updates-20261016/icd10-on-the-go-2026/icon.jpg",
    "screenshots": [
      "/images/app-updates-20261016/icd10-on-the-go-2026/01.webp",
      "/images/app-updates-20261016/icd10-on-the-go-2026/02.webp",
      "/images/app-updates-20261016/icd10-on-the-go-2026/03.webp",
      "/images/app-updates-20261016/icd10-on-the-go-2026/04.webp",
      "/images/app-updates-20261016/icd10-on-the-go-2026/05.webp"
    ],
    "whatsNew": "Version 5.0.1 improves purchase restoration and subscription reliability, adds welcome-back offers for eligible former annual subscribers, and refines the interface. The 2027 edition is available with an active subscription.\n\nReleased 2026-09-24."
  },
  {
    "name": "ICD-10 2027",
    "tagline": "Smart Search 2.0. Your edition, clearly in view.",
    "description": "Find ICD-10-CM codes by code, condition, abbreviation, or everyday term. Smart Search 2.0 considers official Alphabetical Index terms, synonyms, coding notes, explicit laterality, stage, and encounter details, with spelling recovery and explanations for selected matches. Review official instructions, browse code families, and read the bundled FY2027 coding guidelines offline.\n\nFY2026 remains a free earlier reference. An active subscription unlocks FY2027, unlimited favorites, personal note editing, an ad-free experience, comparison of two or three codes, your last 10 completed searches, and additional color themes. Smart Search ranking and iCloud sync are available to everyone. Previously saved favorites and readable notes remain accessible after subscription expiry.\n\nFY2027 is effective October 1, 2026–September 30, 2027. Edition switching preserves your saved reference material. Choose the edition for the date of service and verify code selection against official guidance.",
    "features": [
      "Smart Search 2.0 with abbreviations, synonyms, and spelling recovery",
      "Free FY2026 reference; FY2027 with subscription",
      "Premium side-by-side comparison of two or three codes",
      "Premium recent searches and additional color themes",
      "Favorites and folders with free iCloud sync",
      "Offline code lookup and FY2027 coding guidelines"
    ],
    "version": "5.6",
    "releaseDate": "2026-10-15",
    "slug": "icd10-2026",
    "category": "professional",
    "status": "updated",
    "badge": "Recently updated",
    "storeUrl": "https://apps.apple.com/us/app/icd-10-2027/id878887635",
    "compatibility": "iPhone · iPad",
    "pricing": {
      "en": "FY2026 free · FY2027 with subscription",
      "es": "FY2026 gratis · FY2027 con suscripción"
    },
    "taglineEs": "Smart Search 2.0. Tu edición siempre a la vista.",
    "cardLine": "Smart Search 2.0 and free iCloud sync, with Premium code comparison, recent searches, and FY2027 access.",
    "cardLineEs": "Smart Search 2.0 e iCloud gratis; comparación de códigos, búsquedas recientes y FY2027 con Premium.",
    "icon": "/images/app-updates-20261016/icd10-2026/icon.jpg",
    "screenshots": [
      "/images/app-updates-20261016/icd10-2026/01.webp",
      "/images/app-updates-20261016/icd10-2026/02.webp",
      "/images/app-updates-20261016/icd10-2026/03.webp",
      "/images/app-updates-20261016/icd10-2026/04.webp",
      "/images/app-updates-20261016/icd10-2026/05.webp",
      "/images/app-updates-20261016/icd10-2026/06.webp"
    ],
    "whatsNew": "Version 5.6 adds Premium code comparison, the last 10 completed searches stored on device, and Clinical Teal and Slate Indigo themes. A compact edition header and cleaner layouts make reference work easier. Full Smart Search ranking and iCloud sync remain free.\n\nReleased 2026-10-15."
  },
  {
    "name": "ICD Medical Codes 2027",
    "tagline": "Own your editions. Search and browse offline.",
    "description": "An offline ICD-10-CM reference for iPhone and iPad with separately owned annual editions. Purchase FY2027 once to access its complete database, enhanced abbreviation search, laterality options, billable-code filters, refreshed browsing, and a Saved view with search within folders. Previously purchased editions, including 2026, remain available. There is no subscription; future annual editions are sold separately.\n\nPremium is a separate optional one-time purchase for unlimited favorites and folders, personal notes, search highlighting, and no ads. A 2027 database purchase does not include Premium, and Premium alone does not unlock 2027. Existing owners retain their verified feature rights. iCloud sync is available independently of either purchase.\n\nRead official coding instructions and the bundled FY2027 guidelines offline. Saved codes retain their original edition information. FY2027 applies to October 1, 2026–September 30, 2027; choose the appropriate edition and verify coding decisions against official guidance.",
    "features": [
      "FY2027 database through a separate one-time purchase",
      "Keep access to previously purchased editions",
      "Enhanced 2027 search, laterality, and billable-code filters",
      "Separate optional one-time Premium organization tools",
      "iCloud sync independent of database or Premium purchases",
      "Offline guidelines and saved-code edition provenance"
    ],
    "version": "4.07",
    "releaseDate": "2026-09-20",
    "slug": "icd10-medical-codes",
    "category": "professional",
    "status": "updated",
    "badge": "Recently updated",
    "storeUrl": "https://apps.apple.com/us/app/icd-medical-codes-2027/id723770850",
    "compatibility": "iPhone · iPad",
    "pricing": {
      "en": "FY2027 edition & Premium · Separate one-time purchases",
      "es": "Edición FY2027 y Premium · Compras únicas separadas"
    },
    "taglineEs": "Tus ediciones. Consulta sin conexión.",
    "cardLine": "Own annual editions with one-time purchases, separate optional Premium tools, and independent iCloud sync.",
    "cardLineEs": "Ediciones anuales con compras únicas, herramientas Premium opcionales por separado e iCloud independiente.",
    "icon": "/images/app-updates-20261016/icd10-medical-codes/icon.jpg",
    "screenshots": [
      "/images/app-updates-20261016/icd10-medical-codes/01.webp",
      "/images/app-updates-20261016/icd10-medical-codes/02.webp",
      "/images/app-updates-20261016/icd10-medical-codes/03.webp",
      "/images/app-updates-20261016/icd10-medical-codes/04.webp",
      "/images/app-updates-20261016/icd10-medical-codes/05.webp"
    ],
    "whatsNew": "Version 4.07 introduces the separately purchased FY2027 edition, enhanced Search and Browse, grouped coding instructions, and a refreshed Saved view for 2027 owners. Existing editions and Premium rights are preserved; iCloud sync works independently of purchases.\n\nReleased 2026-09-20."
  },
  {
    slug: "acroly",
    name: "Acroly",
    category: "professional",
    status: "available",
    badge: null,
    tagline: "The modern reference tool designed for the pace of 2026 healthcare",
    taglineEs: "La herramienta de referencia moderna para el ritmo de la salud en 2026",
    cardLine: "Fast access to more than 8,000 medical abbreviations across iPhone, iPad, and Mac.",
    cardLineEs: "Acceso rápido a más de 8.000 abreviaturas médicas en iPhone, iPad y Mac.",
    description: `Medical Abbreviations Acroly — the fastest way to look up medical acronyms and abbreviations on iPhone, iPad, and Mac. Whether you're a nurse, doctor, paramedic, pharmacist, or healthcare student, Acroly gives you instant access to 8,000+ medical abbreviations with lightning-fast full-text search.

KEY FEATURES — 8,000+ medical abbreviations covering clinical, nursing, EMS, pharmacy, and hospital terminology. Lightning-fast full-text search powered by SQLite FTS5 finds any abbreviation in milliseconds, even offline. Organize with custom folders by specialty, rotation, or study topic. Save favorites and add your own custom abbreviations. iCloud Sync (Acroly+) keeps favorites and folders seamless across iPhone, iPad, and Mac. Works offline — perfect for hospitals, ambulances, or anywhere signal is weak.

ACROLY+ SUBSCRIPTION — Unlimited favorites and folders, custom abbreviations, iCloud sync across all devices, and no ads, ever. Monthly $1.99 (3-day free trial); Yearly $9.99 (7-day free trial). Subscriptions auto-renew unless cancelled at least 24 hours before the end of the current period.`,
    icon: "/images/icons/acroly-icon.png",
    screenshots: [
      "/images/screenshots/acroly/01.webp",
      "/images/screenshots/acroly/02.webp",
      "/images/screenshots/acroly/03.webp",
    ],
    storeUrl: "https://apps.apple.com/us/app/medical-abbreviations-acroly/id6758867453",
    features: ["8,000+ Abbreviations", "iCloud Sync", "Custom Folders", "Live Code Sharing", "Pro-Grade Search"],
    compatibility: "iPhone · iPad · Mac",
    pricing: { en: "Free · Acroly+ subscription", es: "Gratis · Suscripción Acroly+" },
  },
  {
    slug: "ob-datum",
    name: "OB Datum: Pregnancy Dating",
    category: "professional",
    status: "available",
    badge: null,
    tagline: "Transparent EDD, IVF, and gestational-age calculations",
    taglineEs: "Cálculos transparentes de FPP, FIV y edad gestacional",
    cardLine: "Transparent pregnancy dating with formulas, equivalent dates, and evidence kept in view.",
    cardLineEs: "Datación del embarazo transparente, con fórmulas, fechas equivalentes y evidencia siempre a la vista.",
    description: "Calculate pregnancy dates from LMP, conception or ovulation, an established EDD, known gestational age, or IVF embryo transfer. Review the source, formula, equivalent dates, and calculation-engine version behind each result. Compare dating evidence while keeping an established EDD locked; the app never changes, averages, or recommends changing it.\n\nVersion 1.1 includes every feature in the paid download: all dating methods, read-only Health import, reconciliation, unlimited anonymous cases, sharing, Calendar export, and custom timeline milestones. No subscription, advertising, or separate feature purchase.\n\nExplore the interactive pregnancy wheel and timeline. Anonymous calculations stay on your device. Independently verify inputs, results, and sources before clinical use.",
    icon: "/images/app-updates-20261016/ob-datum/icon.jpg",
    screenshots: ["/images/app-updates-20261016/ob-datum/01.webp", "/images/app-updates-20261016/ob-datum/02.webp", "/images/app-updates-20261016/ob-datum/03.webp", "/images/app-updates-20261016/ob-datum/04.webp", "/images/app-updates-20261016/ob-datum/05.webp", "/images/app-updates-20261016/ob-datum/06.webp"],
    storeUrl: "https://apps.apple.com/us/app/ob-datum-pregnancy-dating/id6799726065",
    features: ["LMP, conception, known EDD, gestational age, and IVF dating", "Transparent formulas and locked-EDD comparison", "Interactive pregnancy wheel and timeline", "Unlimited anonymous cases stored on device", "Read-only Health import, sharing, and Calendar export", "Every feature included in the paid download; no subscription"],
    compatibility: "iPhone · iPad",
    pricing: { en: "$1.99 US · All features included", es: "$1.99 en EE. UU. · Todas las funciones incluidas" },
    version: "1.1",
    whatsNew: "Version 1.1 — Every feature is included in the downloaded app, with no separate in-app purchase. Clinical sources appear as a non-interactive bibliography. Calculations remain private, offline, and reviewable.\n\nReleased 2026-09-15.",
  },
  {
    slug: "foco-family-budget",
    name: "FOCO: Family Budget",
    category: "professional",
    status: "coming",
    badge: null,
    tagline: "Finance, Order, Control, and Objectives for shared family spending",
    taglineEs: "Finanzas, orden, control y objetivos para el gasto familiar compartido",
    cardLine: "A privacy-first shared budget built for financial clarity without handing over bank credentials.",
    cardLineEs: "Un presupuesto familiar compartido que prioriza la privacidad, sin entregar credenciales bancarias.",
    description: `FOCO is a privacy-first family budget for Apple devices. Plan category limits, record transactions, confirm recurring payments, reconcile your records with your bank, and understand what remains — without giving a budgeting company your bank credentials.

Share a budget privately through iCloud, scan receipts on device, ask questions about verified spending, and use optional Apple Intelligence assistance while keeping every financial change under your control.`,
    icon: "/images/icons/foco-family-budget-icon.png",
    screenshots: [
      "/images/screenshots/foco-family-budget/01-overview.png",
      "/images/screenshots/foco-family-budget/02-quick-add.png",
      "/images/screenshots/foco-family-budget/03-transactions.png",
      "/images/screenshots/foco-family-budget/04-reports.png",
      "/images/screenshots/foco-family-budget/05-settings.png",
    ],
    storeUrl: null,
    features: [
      "Private iCloud budget sharing",
      "Category limits and remaining-to-spend tracking",
      "Guided account reconciliation without bank credentials",
      "On-device receipt understanding and Apple Intelligence",
      "Recurring payments, reports, widgets, Siri, and Apple Watch",
    ],
    compatibility: "iPhone · iPad · Apple Watch",
    pricing: null,
  },
  {
    "name": "ICD 10 2027 Coding Assistant",
    "tagline": "Private, on-device FY2027 coding suggestions for professional review.",
    "description": "Review outpatient documentation with on-device FY2027 ICD-10-CM suggestions. The app identifies documented conditions, grounds candidate codes in the bundled code set, and shows explanations and rule checks for specificity, laterality, seventh characters, Excludes1 conflicts, and sequencing. Uncertain and ruled-out findings appear separately. You review the documentation and confirm every code.\n\nClinical note text is not saved, logged, synced, or sent to a server. Analysis and code search work offline. Anonymous feedback and usage counts exclude clinical notes and code suggestions; collection controls depend on the app version, as described in the privacy policy. Saved reference material can sync through your private iCloud account.\n\nStart free with unlimited search, up to 10 favorites, and 3 eligible note analyses per rolling seven-day period. Fictional samples and short diagnosis lookups do not use that allowance. Premium adds unlimited analyses and favorites, folders, and one-tap saving of suggested code references. Specialty automation is limited. FY2027 applies to October 1, 2026–September 30, 2027.",
    "features": [
      "On-device outpatient documentation analysis with FY2027 suggestions",
      "Review explanations, uncertainty, conflicts, and coding instructions",
      "Offline code search and official reference details",
      "Free search, 10 favorites, and 3 eligible analyses per rolling week",
      "Private iCloud sync for saved references on both plans",
      "Premium unlimited analyses, folders, and batch saving"
    ],
    "version": "1.3",
    "releaseDate": "2026-10-12",
    "slug": "clinical-coding-copilot",
    "category": "professional",
    "status": "updated",
    "badge": "Recently updated",
    "storeUrl": "https://apps.apple.com/us/app/icd-10-2027-coding-assistant/id6789714571",
    "compatibility": "iPhone · iPad",
    "pricing": {
      "en": "Free · Premium subscription",
      "es": "Gratis · Suscripción Premium"
    },
    "taglineEs": "Sugerencias FY2027 privadas en el dispositivo para revisión profesional.",
    "cardLine": "Review outpatient documentation with private, on-device FY2027 suggestions and confirm every code.",
    "cardLineEs": "Revisa documentación ambulatoria con sugerencias FY2027 en el dispositivo y confirma cada código.",
    "icon": "/images/app-updates-20261016/clinical-coding-copilot/icon.jpg",
    "screenshots": [
      "/images/app-updates-20261016/clinical-coding-copilot/01.webp",
      "/images/app-updates-20261016/clinical-coding-copilot/02.webp",
      "/images/app-updates-20261016/clinical-coding-copilot/03.webp",
      "/images/app-updates-20261016/clinical-coding-copilot/04.webp",
      "/images/app-updates-20261016/clinical-coding-copilot/05.webp"
    ],
    "whatsNew": "Version 1.3 adds anonymous feedback without an email address and anonymous usage counts. Clinical notes and code suggestions remain private. See the privacy policy for version-specific collection details.\n\nReleased 2026-10-12."
  },
  {
    slug: "crownspell-word-challenge",
    name: "Crownspell: Word Challenge",
    category: "games",
    status: "coming",
    badge: null,
    tagline: "A bilingual offline word challenge built for daily play, quick rounds, and custom games",
    taglineEs: "Un reto de palabras bilingüe y offline para el juego diario, partidas rápidas y juegos personalizados",
    cardLine: "A bilingual offline word challenge built for daily play, quick rounds, and custom games.",
    cardLineEs: "Un reto de palabras bilingüe y offline para jugar a diario, partidas rápidas y juegos personalizados.",
    description: `Crownspell is an offline-first native word game for iPhone and iPad. Build words from a colorful letter tray in a worldwide Daily Challenge, start a one-tap Quick Play round, or configure a Custom Game around tray size, minimum word length, time limit, difficulty, and English, Spanish, or bilingual play.

A guided first-use lesson teaches the game by doing. The Word Vault keeps discovered words, favorites, and gameplay history on your device, while daily streaks, completion history, sound cues, haptics, and reduced-motion-aware celebrations give every round a satisfying rhythm.

Crownspell includes a bundled production dictionary with 78,052 validated English entries plus a pinned Spanish corpus and published regional variants. Core gameplay works offline. App language and word language are selected independently, so the interface and the dictionary can match different bilingual needs.

Optional features include a completion-aware local Daily reminder and Apple Game Center competition with separate English, Spanish, and bilingual leaderboards. The free version is supported by consent-gated advertising. Crownspell Plus is a one-time purchase that removes ads and unlocks Untimed Custom Games, seven-letter trays, longer minimum words, and instant missed-word reveals.`,
    icon: "/images/icons/crownspell-icon.png",
    screenshots: [
      "/images/screenshots/crownspell/01-play.png",
      "/images/screenshots/crownspell/02-build-word.png",
      "/images/screenshots/crownspell/03-success.png",
      "/images/screenshots/crownspell/04-custom-game.png",
      "/images/screenshots/crownspell/05-word-vault.png",
    ],
    storeUrl: null,
    features: [
      "Worldwide Daily Challenge, Quick Play, and configurable Custom Games",
      "English, Spanish, and bilingual word play with independent app language",
      "78,052 validated English entries plus a production Spanish corpus",
      "Offline-first gameplay, Word Vault, favorites, streaks, sound, and haptics",
      "Optional local reminders and Apple Game Center competition",
      "Crownspell Plus one-time purchase with no subscription",
    ],
    compatibility: "iPhone · iPad",
    pricing: null,
  },
];

export const shippedApps = apps.filter((a) => a.status !== "coming");
export const comingSoonApps = apps.filter((a) => a.status === "coming");
export const professionalApps = apps.filter((a) => a.category === "professional");
export const gamesApps = apps.filter((a) => a.category === "games");

export const clinicianQuote = null;
